import { ArrowRight, Check } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useId } from "react";
import { InlineReferenceMarkdown } from "@/components/ui/inline-reference-markdown";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { buildNextStepsReplyMessage, type NextStepsReplyOption } from "../lib/next-steps-replies";
import { OptionLetterBadge, type OptionLetterTone } from "./assistant-option-letter";

export type NextStepsReplyRowState = "default" | "sending" | "sent" | "unavailable";

export interface AssistantNextStepsRepliesProps {
  options: NextStepsReplyOption[];
  sendingLetter: string | null;
  sentLetter: string | null;
  /** The composer cannot send at all, so every row takes the unavailable treatment. */
  disabled: boolean;
  onSelect: (letter: string) => void;
}

export function getNextStepsReplyRowState({
  letter,
  sendingLetter,
  sentLetter,
  disabled,
}: {
  letter: string;
  sendingLetter: string | null;
  sentLetter: string | null;
  disabled: boolean;
}): NextStepsReplyRowState {
  if (sentLetter && letter === sentLetter) {
    return "sent";
  }
  if (sendingLetter && letter === sendingLetter) {
    return "sending";
  }
  // Past the two branches above, a non-null letter here belongs to a different row: once one
  // option is on its way the rest are no longer offers.
  if (disabled || sendingLetter || sentLetter) {
    return "unavailable";
  }
  return "default";
}

const ROW_STATE_CLASS: Record<NextStepsReplyRowState, string> = {
  default: "cursor-pointer bg-surface-raised hover:bg-accent",
  sending: "bg-surface-blue",
  sent: "bg-surface-blue",
  unavailable: "cursor-default bg-surface-raised",
};

const BADGE_TONE: Record<NextStepsReplyRowState, OptionLetterTone> = {
  default: "default",
  sending: "selected",
  sent: "selected",
  unavailable: "muted",
};

function trailingSlotContent(state: NextStepsReplyRowState): ReactNode {
  switch (state) {
    case "default":
      return (
        <ArrowRight className="size-3.5 text-neutral-dim transition-colors group-hover/next-step-reply:text-foreground" />
      );
    case "sending":
      return <Spinner className="size-3.5 text-blue-content" />;
    case "sent":
      return (
        <span className="flex items-center gap-1 text-[11px] font-medium text-blue-content">
          Sent
          <Check className="size-3.5" />
        </span>
      );
    case "unavailable":
      return null;
  }
}

export function AssistantNextStepsReplies({
  options,
  sendingLetter,
  sentLetter,
  disabled,
  onSelect,
}: AssistantNextStepsRepliesProps) {
  const groupId = useId();
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Shift is not treated as a modifier: it is how an upper-case letter is typed at all, and
    // the letters these rows answer to are upper case.
    if (event.altKey || event.ctrlKey || event.metaKey || event.key.length !== 1) {
      return;
    }
    const pressed = event.key.toUpperCase();
    const option = options.find((candidate) => candidate.letter.toUpperCase() === pressed);
    if (!option) {
      return;
    }
    const state = getNextStepsReplyRowState({
      letter: option.letter,
      sendingLetter,
      sentLetter,
      disabled,
    });
    if (state !== "default") {
      return;
    }
    event.preventDefault();
    onSelect(option.letter);
  };

  return (
    // The letter keys work only while focus is inside the group, so the listener lives here
    // rather than on the document: the same letters are ordinary typing everywhere else.
    // biome-ignore lint/a11y/useSemanticElements: a fieldset is a form-control grouping, with a legend and a disabled cascade over its controls; these rows are transcript affordances that disable themselves one at a time.
    <div
      role="group"
      aria-label="Next steps"
      data-testid="assistant-next-steps-replies"
      className="mt-1.5 flex flex-col gap-1.5"
      onKeyDown={handleKeyDown}
    >
      {options.map((option) => {
        const state = getNextStepsReplyRowState({
          letter: option.letter,
          sendingLetter,
          sentLetter,
          disabled,
        });
        const interactive = state === "default";
        return (
          <button
            key={option.letter}
            type="button"
            data-testid="assistant-next-step-reply"
            data-letter={option.letter}
            data-state={state}
            aria-label={`Reply: ${buildNextStepsReplyMessage(option.letter)}`}
            aria-describedby={`${groupId}-${option.letter}`}
            aria-busy={state === "sending" || undefined}
            aria-disabled={state === "unavailable" || undefined}
            disabled={!interactive}
            onClick={interactive ? () => onSelect(option.letter) : undefined}
            className={cn(
              "group/next-step-reply flex w-full items-start gap-3.5 p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25",
              ROW_STATE_CLASS[state]
            )}
          >
            <OptionLetterBadge letter={option.letter} tone={BADGE_TONE[state]} />
            {/* The 26px floor is what centres a one-line step against the badge beside it; a
                step that wraps grows past it and keeps its first line where the design has it. */}
            <span
              id={`${groupId}-${option.letter}`}
              className="flex min-h-[26px] min-w-0 flex-1 items-center"
            >
              {/* The step sits inside a button, so its chips are static: an interactive inside
                  an interactive breaks both the click target and the a11y tree. */}
              <InlineReferenceMarkdown
                content={option.text}
                mentions="static"
                formatting="markdown"
                className={cn(
                  "min-w-0 break-words text-[13px] leading-[1.5]",
                  state === "unavailable" ? "text-muted-foreground" : "text-foreground"
                )}
              />
            </span>
            <span className="flex h-[26px] shrink-0 items-center">
              {trailingSlotContent(state)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
